/*
 * 苏州海管家物流科技有限公司（https://www.hgj.com/）.
 * 苏公网安备32059002003385号 © 2015-2023 Hgj.com All Rights Reserved. | 苏ICP备15030950号.
 * @Date: 2023.02.03.
 * @Author
 * @Description 企微工作台 - store 文件 授权相关 .
 **/
import router from '@/router'
import urls from '@/api/urls'
import { getData } from '@/api'
import { cloneDeep } from 'lodash'
import { defineStore } from 'pinia'
import {
  clearStorage,
  getStorage,
  removeStorage,
  setStorage,
  clearSession,
} from '@/utils/storage'
import { getAccessToken, clearAccessToken } from '@hgj/common-tools'
import { fetchInsuranceCompanyInfo, fetchWalletDetail } from '@/api/hgj'
import useTabs from '@/hooks/useTabs'
import { useProxyInsurance } from '@/hooks/useProxyInsurance'
import {
  clearBackstageOnlineEditContext,
  getBackstageOnlineEditAccessToken,
  isBackstageOnlineEdit,
} from '@/utils/backstageOnlineEdit'
const { clearApprovalAgent, getApprovalAgent } = useProxyInsurance()
const approvalAgent = getApprovalAgent()
const returnBaseInfo = () => {
  return {
    port: 1,
    phone: '',
    login: '',
    email: '',
    avatar: '',
    userId: '',
    userType: 1,
    userName: '',
    username: '',
    qqNumber: '',
    wxAvatar: '',
    centerLat: '',
    centerLon: '',
    wxNickName: '',
    companyName: '',
    couponCount: 0,
    enterpriseId: '-1',
    enterpriseAuth: 1, // 企业认证， 1：未认证，2：审核中，3：已驳回，4：已认证
    enterpriseState: 1,
    enterpriseList: [],
    enterpriseName: '',
    lclMarketRefresh: false,
    isAgent: false,
    needFillUserInfo: false,
    transportLicenseAuth: 4, // 道路运输许可证，1：未认证，2：审核中，3：已驳回，4：已认证
    hasMonthlyBilling: false,
  }
}

interface IHgjChannelInfo {
  channelCode: string
  insureCompanyCode: string
  insureCompanyName: string
  supplyType: number | null
  supportInvoice: boolean
  supportSettlement: boolean
}

interface IUserInfo {
  /** 是否是代投保企业 */
  isAgent: boolean
  account: string
  crossSupplyCode: string
  email: string
  enterpriseAddress: string
  enterpriseId: string
  enterpriseName: string
  enterpriseState: number
  insureEnterpriseName: string
  insuranceAgreementDisplay: boolean
  insuranceAgreementConfirm: boolean
  phone: string
  supplyCode: string
  systemRole: number
  userId: string
  username: string
  /** 1,"自有", 2,"恪宝", */
  userChannelType: number
  /** 海币余额 */
  seaCoinTopUp: number
  /** 券数量 */
  couponCount: number
  /** 保司发票结算模式 0-非保司发票，1-保司发票（票结），2-保司发票（月结） */
  insuranceInvoiceSettlement: number
  /** 跨境开票模式 0-非保司发票，1-保司发票（票结），2-保司发票（月结） */
  crossInvoiceSettlement: number
  /** 是否存在有效月结账单 */
  hasMonthlyBilling: boolean
  /** 传统分配状态 0-停用 1-启用 */
  assignStatus: number
  /** 跨境分配状态 0-停用 1-启用 */
  crossAssignStatus: number
  liabilitySupplyCode: string
  hgjChannelVOS: IHgjChannelInfo[]
}

const filterChannelsBySettlement = (
  channels: IHgjChannelInfo[],
  settlement: number,
) => {
  if (settlement === 1)
    return channels.filter((channel) => channel.supportInvoice)
  if (settlement === 2)
    return channels.filter((channel) => channel.supportSettlement)
  return channels
}

const filterRoutes = (routes: Array<any>, names: string[]): Array<any> => {
  return routes.filter((route) => {
    if (names.includes(route.name)) {
      return false
    }
    if (route.children) {
      route.children = filterRoutes(route.children, names)
    }
    return true
  })
}

/**
 *@Description setUInfo 前置处理，检查 enterpriseId 是否变化 .
 *@Return .
 */
const checkEnterpriseIdChange = (newUserInfo: any) => {
  if (isBackstageOnlineEdit()) return

  // 获取当前存储的用户信息
  const storedUserInfo = getStorage('userInfo')

  // 如果有旧的用户信息，且 enterpriseId 不一致，则清除所有 sessionStorage
  if (
    storedUserInfo &&
    storedUserInfo.enterpriseId !== newUserInfo?.enterpriseId
  ) {
    clearSession()
    useTabs()?.removeAll()
    // 清除后跳转到传统货运险
    router.push({ name: 'TraditionalInsurance' })
  }
}
interface DictItem {
  code: string
  name: string
  url: string
}

/**
 *@Description 定义  .
 * @Return  .
 **/
export const useAuthStore = defineStore('useAuthStore', {
  state: () => {
    // getter
    const workbenchTab: any = [] // 工作台 路由
    const userInfo: any = returnBaseInfo()
    /** 保险公司信息 */
    const insuranceCompanyInfo: DictItem[] = []
    return {
      token: '',
      userInfo,
      walletInfo: {
        seaCoinTopUp: 0,
        couponCount: 0,
      },
      workbenchTab,
      insuranceCompanyInfo,
    }
  },
  getters: {
    getToken(): string {
      return (
        this.token || getBackstageOnlineEditAccessToken() || getAccessToken()
      )
    },
    getUser(): IUserInfo {
      return { ...getStorage('userInfo'), ...this.userInfo, ...this.walletInfo }
    },
    /** 当前可用的传统货运险通道 */
    traditionalHgjChannelVOS(): IHgjChannelInfo[] {
      const channels = (this.getUser.hgjChannelVOS || []).filter(
        (channel) => channel.supplyType == null || channel.supplyType === 1,
      )
      return filterChannelsBySettlement(
        channels,
        this.getUser.insuranceInvoiceSettlement,
      )
    },
    /** 当前可用的跨境货运险通道 */
    crossHgjChannelVOS(): IHgjChannelInfo[] {
      const channels = (this.getUser.hgjChannelVOS || []).filter(
        (channel) => channel.supplyType === 2,
      )
      return filterChannelsBySettlement(
        channels,
        this.getUser.crossInvoiceSettlement ?? 0,
      )
    },
    /** 是否是自有通道 */
    isOwnChannel(): boolean {
      return this.getUser.userChannelType === 1
    },
    /** 是否是保司发票模式（票结） */
    isEnabledInvoiceMode(): boolean {
      return this.getUser.insuranceInvoiceSettlement === 1
    },
    /** 传统货运险是否是保司发票（月结）模式 */
    isInsurerBillMode(): boolean {
      return this.getUser.insuranceInvoiceSettlement === 2
    },
    /** 传统或跨境货运险是否存在保司发票（月结）模式 */
    hasInsurerBillMode(): boolean {
      return (
        this.getUser.supplyCode === 'XN' ||
        this.getUser.hasMonthlyBilling === true ||
        this.getUser.insuranceInvoiceSettlement === 2 ||
        this.getUser.crossInvoiceSettlement === 2
      )
    },
  },
  actions: {
    // vu ex种不可以有异步， pinia中的action异步、同步皆可
    /**
     *@Description 初始化 路由 .
     *@Return  .
     */
    initRoute() {
      const { supplyCode, crossSupplyCode } = this.getUser
      const { routes } = router.options
      const supplyCodeArr = [...new Set([supplyCode, crossSupplyCode])]
      console.log('routes', routes, supplyCodeArr)

      // let routes = router.options.routes
      // if (supplyCode === 'XN') routes = routes.filter(el => el.name !== 'Liability')
      if (routes) {
        console.log(
          routes.filter((item) => item.name === 'HomeLayout'),
          'HomeLayout',
        )
        const arr = routes
          .filter((item) => item.name === 'HomeLayout')?.[0]
          ?.children?.map((el) => {
            // if(el.name === 'BillManagement') {
            //     el.children = el.children?.filter(val => supplyCodeArr.includes(val.name as string))
            // }
            return el
          })

        const names = []
        // 如果用户不是HGJ，则不显示Invoice
        if (this.userInfo.supplyCode !== 'HGJ') {
          names.push('Invoice')
        }
        // 传统或跨境货运险存在月结模式时，账单列表与消费记录共存
        if (!this.hasInsurerBillMode) {
          names.push('InsurerBillList')
        }
        this.workbenchTab = filterRoutes(cloneDeep(arr), names)
      }
    },
    /**
     *@Description 获取 当前 登录 用户 页面权限 .
     *@Return .
     */
    async setToken() {
      const backstageAccessToken = getBackstageOnlineEditAccessToken()
      const ssObj: string = backstageAccessToken || getAccessToken()
      const sId = ssObj || ''

      this.token = sId
      if (backstageAccessToken) {
        removeStorage('kbToken')
      } else {
        setStorage('kbToken', sId)
      }
    },
    /** 初始化保险公司信息 */
    async initInsuranceCompanyInfo() {
      const { success, data } = await fetchInsuranceCompanyInfo()
      if (success) {
        this.insuranceCompanyInfo = data || []
      }
    },

    /**
     *@Description 获取 用户 信息 .
     *@Return .
     */
    async setUInfo() {
      const res = await getData(urls.getUserDetailUrl)
      // 前置处理：检查 enterpriseId 是否变化
      checkEnterpriseIdChange(res?.data)
      this.userInfo = res?.data || ''
      setStorage('userInfo', res?.data || '')
    },
    /**
     *@Description 获取 用户 信息- 只在应用启动的时候调用 .
     *@Return .
     */
    async setUInfoAtStartApp() {
      let res = await getData(urls.getUserDetailUrl)

      // 如果用户信息返回的代投保企业标识是 false，则清空代投保企业
      if (!res?.data?.isAgent) {
        clearApprovalAgent()
      }

      // 前置处理：检查 enterpriseId 是否变化
      checkEnterpriseIdChange(res?.data)
      this.userInfo = res?.data || ''
      setStorage('userInfo', res?.data || '')
    },
    /**
     *@Description 获取用户的海币和券数量 .
     *@Return .
     */
    async setUserWalletInfo() {
      const [err, res] = await fetchWalletDetail()
      if (!err && res?.success && res?.data) {
        // 更新 walletInfo 中的海币和券数量
        this.walletInfo.seaCoinTopUp = res.data.seaCoinTopUp || 0
        this.walletInfo.couponCount = res.data.couponTotalBalance || 0
      }
      return res
    },
    /**
     *@Description 退出 .
     *@Return .
     */
    async setLogout() {
      clearAccessToken()
      clearBackstageOnlineEditContext()
      this.token = ''
      this.userInfo = returnBaseInfo()
      removeStorage('kbToken')
      removeStorage('userInfo')
      clearStorage()
    },
  },
})
